import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

/**
 * The admin "Active Sessions" card lists `admin_sessions` rows this portal
 * records per browser. The row id is kept in localStorage under this key so a
 * return visit refreshes the same row instead of adding one per page load.
 *
 * These rows are a DISPLAY record, not the session: ending one does not end a
 * Supabase session (see SessionManager's "Sign out other devices" for the real
 * revocation).
 */
const KEY_PREFIX = 'admin_session_';

export function adminSessionKey(userId: string): string {
  return `${KEY_PREFIX}${userId}`;
}

/**
 * Mark every admin session row this browser recorded as inactive, then forget
 * each id whose row was confirmed ended. Called on every admin sign-out path
 * (sidebar, idle timeout, MFA screens) BEFORE the auth session ends, while the
 * update is still authorised (2026-10-13 review, B5: rows stayed active
 * forever after an ordinary sign-out).
 *
 * Best effort and bounded: it never throws and never holds sign-out for more
 * than `timeoutMs`, because a display record must not be the reason someone
 * cannot sign out.
 */
export async function endStoredAdminSessions(timeoutMs = 2000): Promise<void> {
  const stored: Array<{ key: string; userId: string; id: string }> = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(KEY_PREFIX)) continue;
      const id = localStorage.getItem(key);
      if (id) stored.push({ key, userId: key.slice(KEY_PREFIX.length), id });
    }
  } catch {
    return; // storage unavailable: nothing was recorded here either
  }
  if (stored.length === 0) return;

  // The stored id is forgotten only once the row is confirmed inactive. A
  // sign-out from an MFA screen runs at AAL1, where the RESTRICTIVE AAL2
  // policy on admin_sessions matches no row and PostgREST answers 200 [];
  // forgetting the id then would orphan an is_active=true row. Kept, the next
  // AAL2 visit refreshes that same row (SessionManager).
  const work = Promise.all(stored.map(async ({ key, userId, id }) => {
    try {
      const { data, error } = await supabase
        .from('admin_sessions')
        .update({ is_active: false })
        .eq('id', id)
        .eq('user_id', userId)
        .select('id');
      if (error) {
        logger.warn('Could not mark the admin session record inactive', error);
        return;
      }
      if (!data || data.length === 0) return; // not writable from this session
      try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
    } catch (err) {
      logger.warn('Could not mark the admin session record inactive', err);
    }
  }));
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    work,
    new Promise<void>((resolve) => { timer = setTimeout(resolve, timeoutMs); }),
  ]);
  if (timer) clearTimeout(timer);
}
